import React, { useState, useEffect, useCallback } from 'react';
import { collection, doc, setDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import {
  FONT_HEADLINE,
  FONT_BODY,
  FONT_SUBHEADLINE,
  FONT_FOOTNOTE,
  FONT_CAPTION,
} from '../constants/typography';
import {
  BORDER_RADIUS,
  SPACING_TIGHT,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD,
} from '../constants/layout';

function resizeImage(file, maxDimension) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const aspect = img.width / img.height;
        let width, height;
        if (aspect > 1) {
          width = maxDimension;
          height = maxDimension / aspect;
        } else {
          width = maxDimension * aspect;
          height = maxDimension;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          blob => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to compress image'));
          },
          'image/jpeg',
          0.7
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function StarRatingInput({ rating, onChange, size = 36 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <span style={{ display: 'inline-flex', gap: 6, fontSize: size, userSelect: 'none' }}>
      {[1, 2, 3, 4, 5].map(star => (
        <span
          key={star}
          style={{
            cursor: 'pointer',
            color: star <= (hovered || rating) ? '#f5c518' : 'rgba(128,128,128,0.25)',
            transition: 'color 0.1s',
          }}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
        >
          ★
        </span>
      ))}
    </span>
  );
}

export default function WriteReviewModal({ productId, user, onClose }) {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  // Animate in
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => setIsVisible(true));
    return () => { document.body.style.overflow = ''; };
  }, []);

  // ESC to close
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  }, [onClose]);

  const handleFileChange = e => {
    const selected = Array.from(e.target.files || []).slice(0, 5);
    setFiles(selected);
    setPreviews(selected.map(f => URL.createObjectURL(f)));
  };

  const removeImage = index => {
    URL.revokeObjectURL(previews[index]);
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (rating === 0 || isSubmitting) return;
    setIsSubmitting(true);
    setErrorMsg('');

    try {
      const reviewId = crypto.randomUUID();
      let imageURLs = [];

      // Upload images
      if (files.length > 0) {
        imageURLs = await Promise.all(
          files.map(async (file, index) => {
            const resized = await resizeImage(file, 1024);
            const storageRef = ref(
              storage,
              `reviewImages/${reviewId}/${index}.jpg`
            );
            await uploadBytes(storageRef, resized, {
              contentType: 'image/jpeg',
            });
            return getDownloadURL(storageRef);
          })
        );
      }

      const reviewData = {
        id: reviewId,
        productID: productId,
        userID: user.uid,
        userFirstName:
          user.displayName?.split(' ')[0] || 'Customer',
        rating,
        title,
        body,
        imageURLs,
        createdAt: Timestamp.now(),
      };

      await setDoc(doc(db, 'reviews', reviewId), reviewData);
      handleClose();
    } catch (err) {
      console.error('Error submitting review:', err);
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`wrm-overlay ${isVisible ? 'wrm-visible' : ''}`}
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className="wrm-modal">
        <div className="wrm-header">
          <h2 className="wrm-title">Write a Review</h2>
          <button className="wrm-close" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="wrm-form">
          <div className="wrm-field">
            <label className="wrm-label">Rating</label>
            <div style={{ textAlign: 'center' }}>
              <StarRatingInput rating={rating} onChange={setRating} />
            </div>
          </div>

          <div className="wrm-field">
            <label className="wrm-label" htmlFor="review-title">
              Title <span className="wrm-optional">(optional)</span>
            </label>
            <input
              id="review-title"
              type="text"
              className="wrm-input"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Summarize your experience"
            />
          </div>

          <div className="wrm-field">
            <label className="wrm-label" htmlFor="review-body">
              Review <span className="wrm-optional">(optional)</span>
            </label>
            <textarea
              id="review-body"
              className="wrm-textarea"
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Share your experience"
              rows={4}
            />
          </div>

          <div className="wrm-field">
            <label className="wrm-label">
              Photos <span className="wrm-optional">(optional, max 5)</span>
            </label>
            {previews.length > 0 && (
              <div className="wrm-previews">
                {previews.map((src, i) => (
                  <div key={i} className="wrm-preview-item">
                    <img src={src} alt={`Preview ${i + 1}`} className="wrm-preview-img" />
                    <button
                      type="button"
                      className="wrm-preview-remove"
                      onClick={() => removeImage(i)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="wrm-file-input"
            />
          </div>

          {errorMsg && <p className="wrm-error">{errorMsg}</p>}

          <button
            type="submit"
            className="wrm-submit"
            disabled={rating === 0 || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </form>
      </div>

      <style>{`
        .wrm-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.5);
          opacity: 0;
          transition: opacity 0.2s ease;
          padding: ${SPACING_CARD};
        }
        .wrm-overlay.wrm-visible {
          opacity: 1;
        }
        .wrm-modal {
          background: var(--color-background, #fff);
          border-radius: ${BORDER_RADIUS};
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          transform: translateY(20px);
          transition: transform 0.2s ease;
        }
        .wrm-visible .wrm-modal {
          transform: translateY(0);
        }
        .wrm-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: ${SPACING_BUTTON} ${SPACING_CARD};
          border-bottom: 1px solid var(--color-border, #e5e5e5);
          position: sticky;
          top: 0;
          background: var(--color-background, #fff);
          z-index: 1;
        }
        .wrm-title {
          font-size: ${FONT_HEADLINE};
          font-weight: 600;
          margin: 0;
          color: var(--color-text-primary);
        }
        .wrm-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: var(--color-text-secondary);
          padding: ${SPACING_TIGHT} ${SPACING_ELEMENT};
          line-height: 1;
        }
        .wrm-close:hover {
          color: var(--color-text-primary);
        }
        .wrm-form {
          padding: ${SPACING_CARD};
          display: flex;
          flex-direction: column;
          gap: ${SPACING_BUTTON};
        }
        .wrm-field {
          display: flex;
          flex-direction: column;
          gap: ${SPACING_TIGHT};
        }
        .wrm-label {
          font-size: ${FONT_FOOTNOTE};
          font-weight: 600;
          color: var(--color-text-primary);
        }
        .wrm-optional {
          font-weight: 400;
          color: var(--color-text-secondary);
        }
        .wrm-input {
          padding: ${SPACING_COMPACT} ${SPACING_COMPONENT};
          font-size: ${FONT_BODY};
          border: 1px solid var(--color-border, #d1d5db);
          border-radius: ${BORDER_RADIUS};
          background: var(--color-background, #fff);
          color: var(--color-text-primary);
          outline: none;
          transition: border-color 0.15s;
        }
        .wrm-input:focus {
          border-color: var(--brand-primary, #7c3aed);
        }
        .wrm-textarea {
          padding: ${SPACING_COMPACT} ${SPACING_COMPONENT};
          font-size: ${FONT_BODY};
          font-family: inherit;
          border: 1px solid var(--color-border, #d1d5db);
          border-radius: ${BORDER_RADIUS};
          background: var(--color-background, #fff);
          color: var(--color-text-primary);
          outline: none;
          resize: vertical;
          min-height: 100px;
          transition: border-color 0.15s;
        }
        .wrm-textarea:focus {
          border-color: var(--brand-primary, #7c3aed);
        }
        .wrm-previews {
          display: flex;
          gap: ${SPACING_ELEMENT};
          flex-wrap: wrap;
        }
        .wrm-preview-item {
          position: relative;
        }
        .wrm-preview-img {
          width: 72px;
          height: 72px;
          object-fit: cover;
          border-radius: ${BORDER_RADIUS};
        }
        .wrm-preview-remove {
          position: absolute;
          top: -6px;
          right: -6px;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: none;
          background: rgba(0,0,0,0.7);
          color: white;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }
        .wrm-file-input {
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
        }
        .wrm-error {
          color: #ef4444;
          font-size: ${FONT_FOOTNOTE};
          margin: 0;
        }
        .wrm-submit {
          padding: ${SPACING_COMPONENT};
          font-size: ${FONT_BODY};
          font-weight: 600;
          color: white;
          background: var(--brand-primary, #7c3aed);
          border: none;
          border-radius: ${BORDER_RADIUS};
          cursor: pointer;
          transition: opacity 0.15s;
        }
        .wrm-submit:hover:not(:disabled) {
          opacity: 0.9;
        }
        .wrm-submit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 480px) {
          .wrm-overlay {
            padding: 0;
            align-items: flex-end;
          }
          .wrm-modal {
            max-width: 100%;
            max-height: 95vh;
            border-radius: ${BORDER_RADIUS} ${BORDER_RADIUS} 0 0;
          }
        }
      `}</style>
    </div>
  );
}
